/** Map DB curriculum_assignments.status to UI lessonStatusMeta keys. */
export function mapAssignmentStatus(status) {
  if (status === 'in_progress') return 'in-progress';
  return status || 'locked';
}

export function safeProgress(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  if (n > 100) return 100;
  return Math.round(n);
}

function formatDuration(durationSeconds) {
  if (durationSeconds == null || !Number.isFinite(Number(durationSeconds))) {
    return null;
  }
  const minutes = Math.max(1, Math.round(Number(durationSeconds) / 60));
  return `${minutes} min`;
}

function formatLastActivity(iso) {
  if (!iso) return null;

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfThatDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
  const dayDiff = Math.round(
    (startOfToday - startOfThatDay) / (24 * 60 * 60 * 1000)
  );
  const time = date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit'
  });

  if (dayDiff === 0) return `Today, ${time}`;
  if (dayDiff === 1) return `Yesterday, ${time}`;

  return `${date.toLocaleDateString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  })}, ${time}`;
}

function sessionsByLessonId(sessions) {
  return (sessions || []).reduce((map, session) => {
    if (!session.lesson_id) return map;
    const list = map.get(session.lesson_id) || [];
    list.push(session);
    map.set(session.lesson_id, list);
    return map;
  }, new Map());
}

/**
 * Build the parent dashboard curriculum shape from assignments + sessions.
 * Shape matches what SummaryCards / SessionHistory / CurriculumAssignments expect.
 */
export function buildCurriculumFromAssignments(assignments, sessions = []) {
  const byLesson = sessionsByLessonId(sessions);
  const grouped = new Map();

  for (const row of assignments || []) {
    const lesson = row.lessons;
    if (!lesson) continue;

    const subjectName = lesson.subjects?.name || 'Other';
    const lessonSessions = byLesson.get(lesson.id) || [];
    const sortedSessions = [...lessonSessions].sort((a, b) => {
      const aTime = new Date(a.started_at || a.created_at || 0).getTime();
      const bTime = new Date(b.started_at || b.created_at || 0).getTime();
      return bTime - aTime;
    });
    const latest = sortedSessions[0];

    const uiLesson = {
      id: lesson.id,
      assignmentId: row.id,
      order: lesson.sequence_order ?? 0,
      title: lesson.title,
      description: lesson.description || '',
      status: mapAssignmentStatus(row.status),
      progress: safeProgress(row.progress_percent),
      assigned: Boolean(row.assigned),
      sessionCount: lessonSessions.length,
      lastActivity: formatLastActivity(latest?.started_at || latest?.created_at),
      activityOrder: null,
      duration: formatDuration(latest?.duration_seconds),
      latestSessionAt: latest
        ? new Date(latest.started_at || latest.created_at || 0).getTime()
        : null
    };

    if (!grouped.has(subjectName)) {
      grouped.set(subjectName, []);
    }
    grouped.get(subjectName).push(uiLesson);
  }

  const curriculum = [...grouped.entries()].map(([subject, lessons]) => ({
    subject,
    lessons: lessons.sort((a, b) => a.order - b.order)
  }));

  // activityOrder: lower = more recent (matches prior mock SummaryCards sort)
  const touched = curriculum
    .flatMap((group) => group.lessons)
    .filter((lesson) => (
      lesson.status === 'completed' || lesson.status === 'in-progress'
    ))
    .filter((lesson) => lesson.latestSessionAt != null)
    .sort((a, b) => b.latestSessionAt - a.latestSessionAt);

  touched.forEach((lesson, index) => {
    lesson.activityOrder = index + 1;
  });

  curriculum.forEach((group) => {
    group.lessons.forEach((lesson) => {
      delete lesson.latestSessionAt;
    });
  });

  return curriculum;
}

export function childDisplayName(child) {
  return child?.name || 'Your child';
}
